import { Component, OnDestroy, Inject, ViewChild, ElementRef, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { Subject } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import * as pdfjsLib from 'pdfjs-dist';

// Set the worker source - use CDN for reliable cross-origin loading
// This matches the version installed: 3.11.174
pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

// File type detection result
type FileType = 'image' | 'pdf' | 'unknown';

@Component({
    selector: 'app-document-viewer',
    templateUrl: './modal-image.component.html',
    styleUrls: ['./modal-image.component.scss'],
})
export class DocumentViewerComponent implements OnDestroy, AfterViewInit {
    @ViewChild('pdfCanvas') pdfCanvas: ElementRef<HTMLCanvasElement>;

    destroy$ = new Subject();

    // File type detection
    detectedFileType: FileType = 'unknown';
    isDetectingType = true;
    detectedImageUrl: string | null = null;

    // Viewer properties (shared by images and PDFs)
    zoomLevel = 1;
    rotation = 0;
    minZoom = 0.5;
    maxZoom = 5;
    zoomStep = 0.5;

    // Drag and pan properties
    isDragMode = true;
    isDragging = false;
    panX = 0;
    panY = 0;
    startX = 0;
    startY = 0;
    initialPanX = 0;
    initialPanY = 0;

    // PDF specific properties
    pdfDocument: pdfjsLib.PDFDocumentProxy | null = null;
    currentPage = 1;
    totalPages = 0;
    pdfLoading = false;
    pdfLoadError = false;
    baseScale = 1.5; // Base rendering scale for PDF pages

    constructor(
        @Inject(MAT_DIALOG_DATA) public data,
        private dialogRef: MatDialogRef<DocumentViewerComponent>,
        private sanitizer: DomSanitizer,
        private cdr: ChangeDetectorRef,
    ) {}

    ngAfterViewInit(): void {
        this.detectFileType();
    }

    /**
     * Extract the file path from a URL, removing query parameters
     */
    private getUrlPath(url: string): string {
        try {
            const urlObj = new URL(url);
            return urlObj.pathname;
        } catch {
            // If URL parsing fails, try to strip query params manually
            const queryIndex = url.indexOf('?');
            return queryIndex > -1 ? url.substring(0, queryIndex) : url;
        }
    }

    /**
     * Check if URL has a known image extension
     */
    private hasImageExtension(url: string): boolean {
        const imageExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.jfif', '.jpe', '.svg', '.tiff', '.tif', '.ico'];
        const path = this.getUrlPath(url);
        const extension = path.substring(path.lastIndexOf('.')).toLowerCase();
        return imageExtensions.includes(extension);
    }

    /**
     * Check if URL has a PDF extension
     */
    private hasPdfExtension(url: string): boolean {
        const path = this.getUrlPath(url);
        const extension = path.substring(path.lastIndexOf('.')).toLowerCase();
        return extension === '.pdf';
    }

    /**
     * Detect file type using extension first, then content-type header as fallback
     */
    async detectFileType(): Promise<void> {
        const url = this.data.src;

        // First, try to detect by extension
        if (this.hasImageExtension(url)) {
            this.detectedFileType = 'image';
            this.detectedImageUrl = url;
            this.isDetectingType = false;
            this.cdr.detectChanges();
            return;
        }

        if (this.hasPdfExtension(url)) {
            this.detectedFileType = 'pdf';
            this.isDetectingType = false;
            this.cdr.detectChanges();
            this.loadPdf();
            return;
        }

        // If extension is unknown, fetch content-type header
        try {
            const response = await fetch(url, { method: 'HEAD' });
            const contentType = response.headers.get('content-type') || '';

            if (contentType.startsWith('image/')) {
                this.detectedFileType = 'image';
                this.detectedImageUrl = url;
            } else if (contentType === 'application/pdf') {
                this.detectedFileType = 'pdf';
                // Load PDF after detection
                setTimeout(() => this.loadPdf(), 0);
            } else {
                // Unknown type - use iframe fallback
                this.detectedFileType = 'unknown';
            }
        } catch {
            // On error, default to unknown (iframe fallback)
            this.detectedFileType = 'unknown';
        }

        this.isDetectingType = false;
        this.cdr.detectChanges();
    }

    /**
     * Helper for template - check if showing image view
     */
    isImage(): boolean {
        return this.detectedFileType === 'image';
    }

    /**
     * Helper for template - check if showing PDF view
     */
    isPdf(): boolean {
        return this.detectedFileType === 'pdf';
    }

    /**
     * Helper for template - show controls for image or PDF
     */
    showControls(): boolean {
        return this.detectedFileType === 'image' || this.detectedFileType === 'pdf';
    }

    getSafeUrl(url: string): SafeResourceUrl {
        return this.sanitizer.bypassSecurityTrustResourceUrl(url);
    }

    // PDF loading and rendering
    async loadPdf(): Promise<void> {
        try {
            this.pdfLoading = true;
            this.pdfLoadError = false;
            this.cdr.detectChanges();

            // Fetch the PDF as an ArrayBuffer
            const response = await fetch(this.data.src);
            if (!response.ok) {
                throw new Error(`Failed to fetch PDF: ${response.status} ${response.statusText}`);
            }

            const arrayBuffer = await response.arrayBuffer();
            const uint8Array = new Uint8Array(arrayBuffer);

            const loadingTask = pdfjsLib.getDocument({
                data: uint8Array,
                cMapUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/cmaps/',
                cMapPacked: true,
            });
            this.pdfDocument = await loadingTask.promise;
            this.totalPages = this.pdfDocument.numPages;
            this.currentPage = 1;

            await this.renderPage(this.currentPage);
            this.pdfLoading = false;
            this.cdr.detectChanges();
        } catch (error) {
            console.error('Error loading PDF:', error);
            this.pdfLoadError = true;
            this.pdfLoading = false;
            this.cdr.detectChanges();
        }
    }

    async renderPage(pageNumber: number): Promise<void> {
        if (!this.pdfDocument || !this.pdfCanvas) {
            return;
        }

        try {
            const page = await this.pdfDocument.getPage(pageNumber);
            const canvas = this.pdfCanvas.nativeElement;
            const context = canvas.getContext('2d');

            if (!context) {
                return;
            }

            const viewport = page.getViewport({ scale: this.baseScale });
            canvas.width = viewport.width;
            canvas.height = viewport.height;

            const renderContext = {
                canvasContext: context,
                viewport: viewport,
            };

            await page.render(renderContext).promise;
        } catch (error) {
            console.error('Error rendering PDF page:', error);
        }
    }

    // Page navigation for PDFs
    previousPage(): void {
        if (this.currentPage > 1) {
            this.currentPage--;
            this.resetPan();
            this.renderPage(this.currentPage);
        }
    }

    nextPage(): void {
        if (this.currentPage < this.totalPages) {
            this.currentPage++;
            this.resetPan();
            this.renderPage(this.currentPage);
        }
    }

    goToPage(page: number): void {
        if (page >= 1 && page <= this.totalPages) {
            this.currentPage = page;
            this.resetPan();
            this.renderPage(this.currentPage);
        }
    }

    private resetPan(): void {
        this.panX = 0;
        this.panY = 0;
    }

    // Shared controls (works for both images and PDFs)
    zoomIn(): void {
        if (this.zoomLevel < this.maxZoom) {
            this.zoomLevel += this.zoomStep;
        }
    }

    zoomOut(): void {
        if (this.zoomLevel > this.minZoom) {
            this.zoomLevel -= this.zoomStep;
        }
    }

    resetZoom(): void {
        this.zoomLevel = 1;
        this.resetPan();
    }

    rotateLeft(): void {
        this.rotation = (this.rotation - 90) % 360;
    }

    rotateRight(): void {
        this.rotation = (this.rotation + 90) % 360;
    }

    resetRotation(): void {
        this.rotation = 0;
    }

    getTransform(): string {
        return `scale(${this.zoomLevel}) rotate(${this.rotation}deg)`;
    }

    async downloadFile(): Promise<void> {
        try {
            const response = await fetch(this.data.src);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            // Extract filename from URL or use default
            const urlPath = this.data.src.split('?')[0];
            const filename = urlPath.substring(urlPath.lastIndexOf('/') + 1) || 'document';
            link.download = filename;

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            // Silently handle download errors
        }
    }

    // Drag functionality
    toggleDragMode(): void {
        this.isDragMode = !this.isDragMode;
    }

    onMouseDown(event: MouseEvent): void {
        if (!this.isDragMode) {
            return;
        }

        this.isDragging = true;
        this.startX = event.clientX;
        this.startY = event.clientY;
        this.initialPanX = this.panX;
        this.initialPanY = this.panY;
        event.preventDefault();
    }

    onMouseMove(event: MouseEvent): void {
        if (!this.isDragging || !this.isDragMode) {
            return;
        }

        const deltaX = event.clientX - this.startX;
        const deltaY = event.clientY - this.startY;

        this.panX = this.initialPanX + deltaX;
        this.panY = this.initialPanY + deltaY;
    }

    onMouseUp(): void {
        this.isDragging = false;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    close(): void {
        this.dialogRef.close();
    }
}
